import { useEffect, useState } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

const SLIDES = [
  {
    url: "/images/finish-line-2025.png",
    tag: "The starting line",
    title: "Run to Empower",
    subtitle: "Entrepreneurs 5K",
  },
  {
    url: "/images/youth-runner-2.jpg",
    tag: "Youth · Bibs #232 · #242 · #239",
    title: "Kids leading",
    subtitle: "the way",
  },
  {
    url: "/images/adult-runners.jpg",
    tag: "Adult walkers · 2025",
    title: "Every step",
    subtitle: "fuels a founder",
  },
  {
    url: "/images/volunteers-2025.png",
    tag: "Volunteer crew",
    title: "Team energy,",
    subtitle: "team purpose",
  },
  {
    url: "/images/executive-winners.png",
    tag: "2025 Podium",
    title: "Run for",
    subtitle: "the medal",
  },
  {
    url: "/images/candid-runners.jpg",
    tag: "November 7, 2026",
    title: "See you at",
    subtitle: "the start line",
  },
];

const DURATION = 4500; // ms per slide

export default function PromoVideo({ onRegister }) {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!playing) return;
    setProgress(0);
    const startedAt = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const p = Math.min(100, (elapsed / DURATION) * 100);
      setProgress(p);
    }, 60);
    const advance = setTimeout(() => setActive((i) => (i + 1) % SLIDES.length), DURATION);
    return () => {
      clearInterval(tick);
      clearTimeout(advance);
    };
  }, [active, playing]);

  return (
    <section
      id="promo-video"
      data-testid="section-promo-video"
      className="relative bg-black py-14 md:py-20 border-t border-[#27272A] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 border border-[#EC008C]/60 bg-[#EC008C]/10 px-3 py-1 mb-3">
              <span className="w-2 h-2 rounded-full bg-[#EC008C] animate-pulse" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#EC008C] font-bold">Watch · 2025 Recap</span>
            </div>
            <h3 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight text-white">
              This is what a <span className="text-[#00A651]">movement</span> looks like.
            </h3>
          </div>
        </div>

        <div className="relative aspect-video w-full bg-black border border-[#27272A] overflow-hidden shadow-[0_0_60px_rgba(0,166,81,0.15)]">
          {/* Slides with cross-fade + Ken Burns */}
          {SLIDES.map((s, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out ${i === active ? "opacity-100" : "opacity-0"}`}
              aria-hidden={i !== active}
            >
              <img
                src={s.url}
                alt={s.tag}
                loading={i < 2 ? "eager" : "lazy"}
                className={`w-full h-full object-cover object-center ${i === active ? "kenburns" : ""}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/20" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

              {/* Overlay text with entrance animation */}
              <div className={`absolute left-6 md:left-14 top-1/2 -translate-y-1/2 max-w-lg ${i === active ? "slide-in" : "opacity-0"}`}>
                <div className="text-[10px] md:text-xs uppercase tracking-[0.28em] text-[#00A651] font-bold mb-3">
                  {s.tag}
                </div>
                <div className="font-display text-4xl md:text-6xl lg:text-7xl font-black uppercase leading-[0.9] tracking-tight text-white">
                  {s.title}
                  <br />
                  <span className="text-[#EC008C] text-shadow-glow-pink">{s.subtitle}</span>
                </div>
              </div>
            </div>
          ))}

          {/* Fixed event tag (top-right) */}
          <div className="absolute top-4 right-4 z-20 bg-[#EC008C]/95 backdrop-blur-sm text-white px-3 py-1.5 font-display font-black uppercase text-xs md:text-sm tracking-widest shadow-lg">
            Nov 7, 2026 · 9 AM
          </div>

          {/* LIVE recording indicator */}
          <div className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-black/70 backdrop-blur-sm px-2.5 py-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] uppercase tracking-widest text-white font-bold">Live · 2025 Recap</span>
          </div>

          {/* Slide indicator dots */}
          <div className="absolute bottom-16 md:bottom-20 left-6 md:left-14 z-20 flex items-center gap-2">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => setActive(i)}
                className={`h-1 transition-all ${i === active ? "w-10 bg-[#00A651]" : "w-4 bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>

          {/* Video-like control bar */}
          <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black via-black/70 to-transparent px-4 md:px-6 py-3 md:py-4">
            <div className="flex items-center gap-3 md:gap-4">
              <button
                onClick={() => setPlaying((p) => !p)}
                aria-label={playing ? "Pause" : "Play"}
                data-testid="promo-play-btn"
                className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-[#00A651] hover:bg-[#1FBF3E] text-black flex items-center justify-center transition-transform hover:scale-105 shrink-0"
              >
                {playing ? <Pause className="w-4 h-4 md:w-5 md:h-5" /> : <Play className="w-4 h-4 md:w-5 md:h-5 ml-0.5" />}
              </button>
              <div className="flex-1 h-1 bg-white/20 relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-[#EC008C]" style={{ width: `${progress}%`, transition: "width 60ms linear" }} />
              </div>
              <div className="hidden md:block text-[10px] uppercase tracking-widest text-white/70 font-bold whitespace-nowrap">
                {String(active + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
              </div>
              <button
                onClick={() => setMuted((m) => !m)}
                aria-label={muted ? "Unmute" : "Mute"}
                className="w-8 h-8 md:w-10 md:h-10 border border-white/30 hover:border-white text-white flex items-center justify-center transition-colors shrink-0"
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <button
                onClick={onRegister}
                className="hidden sm:inline-flex items-center gap-2 bg-[#EC008C] hover:bg-[#FF1FA8] text-white font-bold uppercase tracking-widest text-[10px] md:text-xs px-4 md:px-5 py-2 md:py-2.5 transition-all hover:-translate-y-0.5 shrink-0"
              >
                Register →
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-[#71717A] uppercase tracking-widest">
          <span>Auto-playing recap · {SLIDES.length} scenes · {(DURATION / 1000).toFixed(1)}s each</span>
          <button
            onClick={onRegister}
            className="sm:hidden bg-[#EC008C] hover:bg-[#FF1FA8] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 transition-all"
          >
            Register →
          </button>
        </div>
      </div>
    </section>
  );
}
